import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export { dayjs };

/*
 * Formatting and comparison helpers. Ported from the markup plus
 * frontend/app/utils/formatters.js.
 *
 * Note on units: the backend returns durations in MILLISECONDS. The markup's mock
 * data used minutes. Everything here takes an explicit unit so the two never get
 * mixed up silently — a duration off by 60× is the kind of bug that looks
 * plausible on screen.
 */

/* ---- Durations ----------------------------------------------------------- */

/** 462 minutes → "7h 42m";  38 → "38m" */
export function formatMinutes(minutes) {
    const total = Math.max(0, Math.round(minutes || 0));
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

/** Backend durations are milliseconds. */
export function formatDuration(ms) {
    return formatMinutes((ms || 0) / 60000);
}

/** 294 seconds → "4m 54s" — for idle periods, where seconds matter. */
export function formatSeconds(seconds) {
    const total = Math.max(0, Math.round(seconds || 0));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

/* ---- Clock and dates ----------------------------------------------------- */

/** Minutes since midnight → "9:05 AM". Used by schedules and attendance. */
export function formatClock(minutesSinceMidnight) {
    const total = Math.round(minutesSinceMidnight || 0);
    const h = Math.floor(total / 60) % 24;
    const m = total % 60;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

/** "HH:mm" → "9:05 AM" */
export function formatHHmm(hhmm) {
    const [h, m] = String(hhmm ?? "0:0").split(":").map(Number);
    return formatClock((h || 0) * 60 + (m || 0));
}

export const formatDate = (value, pattern = "DD MMM YYYY") =>
    value ? dayjs(value).format(pattern) : "—";

export const formatTime = (value) => (value ? dayjs(value).format("h:mm A") : "—");

export const formatDateTime = (value) => (value ? dayjs(value).format("DD MMM YYYY, h:mm A") : "—");

/*
 * With seconds. Used wherever the subject is a single CAPTURE — a screenshot,
 * a key-log interval — because those arrive several to the minute and a
 * minute-precision label makes five different moments look like one.
 *
 * Deliberately separate from formatDateTime: a time entry or a schedule reads
 * better without the seconds, and they are not competing for identity.
 */
export const formatTimeSeconds = (value) => (value ? dayjs(value).format("h:mm:ss A") : "—");

export const formatDateTimeSeconds = (value) =>
    value ? dayjs(value).format("DD MMM YYYY, h:mm:ss A") : "—";

/**
 * "2 min ago" for anything recent, an absolute time for today, a date beyond
 * that. A bare relative time is useless for "18 Jul" and a bare date is useless
 * for "just now", so pick per distance.
 */
export function formatLastActive(value) {
    if (!value) return "Never";

    const d = dayjs(value);
    if (!d.isValid()) return "—";

    const minutes = dayjs().diff(d, "minute");
    if (minutes < 60) return d.fromNow();
    if (d.isSame(dayjs(), "day")) return d.format("h:mm A");
    return d.format("D MMM YYYY");
}

/** The timezone the browser is in — sent as the x-user-timezone header. */
export function browserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return null;
    }
}

/* ---- Numbers ------------------------------------------------------------- */

export const formatNumber = (n) => (n ?? 0).toLocaleString();

export const formatPercent = (value, digits = 0) =>
    `${(value ?? 0).toFixed(digits)}%`;

/** Share of a whole, guarding against divide-by-zero. */
export const percentOf = (part, total) => (total ? ((part || 0) / total) * 100 : 0);

/*
 * Hours → a short axis tick.
 *
 * Under an hour the honest unit is minutes ("45m"); above it, hours with the
 * half hour when there is one ("2h", "2.5h"). Five ticks reading "3h 29m" is a
 * wall of text — the tooltip is where the exact figure belongs.
 */
export function formatHoursTick(hours) {
    const value = Number(hours) || 0;
    if (value < 1) return `${Math.round(value * 60)}m`;

    /*
     * Trimmed, NOT rounded. A gridline at 2.25 labelled "2.5h" is an axis
     * lying about where it is; trailing zeros just make it longer.
     */
    return `${Number(value.toFixed(2))}h`;
}

/* ---- Desktop app versions -----------------------------------------------
 * "1.2.10" must sort AFTER "1.2.8". A plain string compare gets that backwards,
 * which is why the current app carries its own version-aware sorter.
 * ---------------------------------------------------------------------- */

export function parseVersion(value) {
    return String(value || "")
        .replace(/^v/i, "")
        .split(".")
        .map((n) => parseInt(n, 10) || 0);
}

export function compareVersion(a, b) {
    const va = parseVersion(a);
    const vb = parseVersion(b);
    for (let i = 0; i < Math.max(va.length, vb.length); i++) {
        const diff = (va[i] || 0) - (vb[i] || 0);
        if (diff) return diff;
    }
    return 0;
}

/** Sort key for a version, so tables can order by it numerically. */
export const versionSortKey = (value) =>
    parseVersion(value).reduce((acc, part) => acc * 1000 + part, 0);

/**
 * Four buckets, because "old" means different things operationally: a pre-2.0
 * build predates the data-stall fixes and needs chasing down, whereas one patch
 * behind is routine. See appDoc/ for the field reports.
 */
/*
 * A tier is a COMPARISON, so it needs something to compare against.
 *
 * `latest` comes from the published-builds catalog, and until someone publishes
 * one it is null. parseVersion(null) is [0,0,0], so every installed version
 * counted as "greater than or equal to latest" and the page labelled the whole
 * fleet "Latest" — including 1.2.8, which predates every data-stall fix. An
 * admin reading that would leave a broken machine alone.
 *
 * With no reference the honest answer is that we do not know, not that
 * everything is fine.
 */
export function versionTier(appVersion, latest) {
    if (!appVersion) return "none";
    if (!latest) return "unknown";
    if (compareVersion(appVersion, latest) >= 0) return "current";
    return parseVersion(appVersion)[0] < parseVersion(latest)[0] ? "stale" : "behind";
}

/* ---- Misc ---------------------------------------------------------------- */

export function hostOf(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return "";
    }
}

export const osIcon = (os) =>
    /mac/i.test(os) ? "🍎" : /win/i.test(os) ? "🪟" : /linux/i.test(os) ? "🐧" : "💻";
